import { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { backfillOrganizerId, checkBackfillNeeded } from '../services/backfillService';
import { Database, AlertCircle, CheckCircle, Play, Search } from 'lucide-react';
import { toast } from 'sonner';

export const AdminTools = () => {
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [backfillResult, setBackfillResult] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    
    try {
      const result = await checkBackfillNeeded();
      setCheckResult(result);
      
      if (result.needsBackfill > 0) {
        toast.info(`${result.needsBackfill} eventos precisam de backfill`);
      } else {
        toast.success('Todos os eventos já têm organizerId!');
      }
    } catch (error) {
      toast.error('Erro ao verificar eventos');
      console.error(error);
    } finally {
      setChecking(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Tem certeza que deseja executar o backfill? Esta ação irá atualizar múltiplos eventos.')) {
      return;
    }

    setRunning(true);
    setBackfillResult(null);
    
    try {
      const result = await backfillOrganizerId();
      setBackfillResult(result);
      
      if (result.success) {
        toast.success(`Backfill concluído! ${result.updatedCount} eventos atualizados.`);
      } else {
        toast.error('Erro ao executar backfill');
      }
    } catch (error) {
      toast.error('Erro ao executar backfill');
      console.error(error);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 p-4 pt-16 md:p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Admin Tools
            </h1>
            <p className="text-muted-foreground">Ferramentas de manutenção e migração do sistema</p>
          </div>

          {/* Backfill organizerId Tool */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Database className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-semibold mb-2">Backfill organizerId</h2>
                <p className="text-muted-foreground mb-4">
                  Adiciona o campo <code className="bg-muted px-2 py-1 rounded">organizerId</code> em eventos antigos que só possuem <code className="bg-muted px-2 py-1 rounded">createdBy</code>.
                </p>
                
                <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    O que esta ferramenta faz:
                  </h3>
                  <ul className="space-y-1 text-sm">
                    <li>✅ Busca eventos sem <code>organizerId</code></li>
                    <li>✅ Copia valor de <code>createdBy</code> para <code>organizerId</code></li>
                    <li>✅ NÃO sobrescreve <code>organizerId</code> existente</li>
                    <li>✅ Processa em lotes para melhor performance</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleCheck}
                    disabled={checking || running}
                    variant="outline"
                    className="gap-2"
                    data-testid="check-backfill-btn"
                  >
                    <Search className="w-4 h-4" />
                    {checking ? 'Verificando...' : 'Verificar Eventos'}
                  </Button>
                  
                  <Button
                    onClick={handleBackfill}
                    disabled={checking || running}
                    className="gap-2"
                    data-testid="run-backfill-btn"
                  >
                    <Play className="w-4 h-4" />
                    {running ? 'Executando...' : 'Executar Backfill'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Check Results */}
            {checkResult && (
              <div className="border-t border-border pt-6">
                <h3 className="font-semibold mb-3">Resultado da Verificação:</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">Total de Eventos</p>
                    <p className="text-2xl font-bold">{checkResult.totalEvents}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                    <p className="text-sm text-green-700 dark:text-green-300 mb-1">Já têm organizerId</p>
                    <p className="text-2xl font-bold text-green-600">{checkResult.hasOrganizerId}</p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-950 rounded-lg p-4">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">Precisam de Backfill</p>
                    <p className="text-2xl font-bold text-yellow-600">{checkResult.needsBackfill}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-950 rounded-lg p-4">
                    <p className="text-sm text-red-700 dark:text-red-300 mb-1">Sem createdBy</p>
                    <p className="text-2xl font-bold text-red-600">{checkResult.noCreatedBy}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Backfill Results */}
            {backfillResult && (
              <div className="border-t border-border pt-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  {backfillResult.success ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Backfill Concluído com Sucesso!
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      Backfill Falhou
                    </>
                  )}
                </h3>
                {backfillResult.success ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">Total</p>
                      <p className="text-2xl font-bold">{backfillResult.totalEvents}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                      <p className="text-sm text-green-700 dark:text-green-300 mb-1">Atualizados</p>
                      <p className="text-2xl font-bold text-green-600">{backfillResult.updatedCount}</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">Ignorados</p>
                      <p className="text-2xl font-bold text-blue-600">{backfillResult.skippedCount}</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-950 rounded-lg p-4">
                      <p className="text-sm text-red-700 dark:text-red-300 mb-1">Erros</p>
                      <p className="text-2xl font-bold text-red-600">{backfillResult.errorCount}</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 dark:bg-red-950 rounded-lg p-4">
                    <p className="text-sm text-red-700 dark:text-red-300">{backfillResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Informações Importantes
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
              <li>• Esta ferramenta deve ser executada apenas UMA VEZ após a migração do sistema</li>
              <li>• O backfill é seguro e não sobrescreve dados existentes</li>
              <li>• Use "Verificar Eventos" primeiro para ver quantos eventos serão afetados</li>
              <li>• Os eventos são processados em lotes de 500 para melhor performance</li>
              <li>• Verifique o console do navegador (F12) para logs detalhados</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

